import type { ChoiceResult } from '@core-api/presentation/types/response';
import type { QuestionChoice, QuestionSubType } from '@mentimeter/http-clients';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import {
  NonInteractiveVisualizationWrapper,
  TitleSectionDataHandler,
} from '@mentimeter/question-modules-shared';
import type { ChoicesT, EmptyT } from '@mentimeter/question-modules-types';
import { type PropsWithChildren } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import { BarsDataHandler } from './data-handlers/BarsDataHandler';
import { PieDataHandler } from './data-handlers/PieDataHandler';
import { SegmentedBarsDataHandler } from './data-handlers/SegmentedBarsDataHandler';
import Dots from './Dots';
import { ScreenReaderTable } from './components/ScreenReaderTable/ScreenReaderTable';

interface PresentationModuleProps {
  identifiedResults: ChoiceResult[] | null;
  isSegmented: boolean;
  shouldShowCorrect: boolean;
  results: ChoicesT | EmptyT;
  choices: QuestionChoice[];
  sub_type: Extract<QuestionSubType, 'pie' | 'donut' | 'choices' | 'dots'>;
  shouldHideResults: boolean;
  shouldShowPercent: boolean;
}

function getDisplayVisualizationComponentType(
  choices: QuestionChoice[],
  isSegmented: boolean,
  sub_type: 'choices' | 'donut' | 'dots' | 'pie' | null,
) {
  if (choices.length > 12) {
    return isSegmented ? 'segmented-bars' : 'bars';
  }
  if (sub_type === 'pie' || sub_type === 'donut') {
    return 'pie';
  }
  if (sub_type === 'dots') {
    return 'dots';
  }

  return isSegmented ? 'segmented-bars' : 'bars';
}

export function ChoicesPresentation({
  identifiedResults,
  isSegmented,
  shouldShowCorrect,
  results: rawResults,
  choices,
  sub_type,
  shouldHideResults,
  shouldShowPercent,
  children,
}: PropsWithChildren<PresentationModuleProps>) {
  const { InteractiveCanvasComponents } = usePresentationContext();

  const displayVisualizationComponentType =
    getDisplayVisualizationComponentType(choices, isSegmented, sub_type);

  const resultsWithFallback = choices.map((choice) => {
    const results = rawResults?.results ?? [];
    const result = results.find(({ id }) => id === choice.id);
    const fallbackResult = {
      id: choice.id,
      label: choice.label,
      score: [0],
    };
    return { ...(result ?? fallbackResult), label: choice.label };
  });

  const results = {
    respondents: rawResults?.respondents ?? 0,
    previousRespondents: 0,
    results: resultsWithFallback,
    previousResults: [],
  };

  const ConditionalWrapper =
    InteractiveCanvasComponents?.CanvasVisualizationArea
      ? InteractiveCanvasComponents?.CanvasVisualizationArea
      : NonInteractiveVisualizationWrapper;

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="choices-container"
      alignItems="center"
      justifyContent="center"
    >
      <TitleSectionDataHandler showTextSizeDropdown={true} />
      <ConditionalWrapper flex="1" width="100%">
        {children || (
          <>
            {displayVisualizationComponentType === 'bars' && (
              <BarsDataHandler
                showCorrect={shouldShowCorrect}
                results={results}
                identifiedResults={identifiedResults}
                shouldShowPercent={shouldShowPercent}
                shouldHideResults={shouldHideResults}
                choices={choices}
              />
            )}
            {displayVisualizationComponentType === 'segmented-bars' && (
              <SegmentedBarsDataHandler
                showCorrect={shouldShowCorrect}
                results={results}
                identifiedResults={identifiedResults}
                shouldShowPercent={shouldShowPercent}
                shouldHideResults={shouldHideResults}
                choices={choices}
              />
            )}
            {displayVisualizationComponentType === 'pie' && (
              <PieDataHandler
                showCorrect={shouldShowCorrect}
                results={results}
                identifiedResults={identifiedResults}
                shouldShowPercent={shouldShowPercent}
                shouldHideResults={shouldHideResults}
                choices={choices}
                visualizationType={sub_type === 'donut' ? 'donut' : 'pie'}
              />
            )}
            {displayVisualizationComponentType === 'dots' && (
              <Dots
                showCorrect={shouldShowCorrect}
                results={results}
                shouldShowPercent={shouldShowPercent}
                shouldHideResults={shouldHideResults}
                choices={choices}
              />
            )}
            <ScreenReaderOnly>
              <ScreenReaderTable
                choices={choices}
                results={resultsWithFallback}
                shouldShowCorrect={shouldShowCorrect}
                shouldShowScorePercentage={
                  shouldShowPercent
                    ? { percentOf: rawResults?.respondents }
                    : false
                }
                shouldHideResults={shouldHideResults}
              />
            </ScreenReaderOnly>
          </>
        )}
      </ConditionalWrapper>
    </Box>
  );
}
